import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { apiClient } from '@/api/axiosClient';
import PageHeader from '@/components/common/PageHeader';
import DataTable from '@/components/common/DataTable';
import type { DataTableColumn } from '@/components/common/DataTable';
import StatusChip from '@/components/common/StatusChip';
import { useDateFormat } from '@/hooks/useDateFormat';
import { useProcessConfiguration } from '@/hooks/useProcessConfiguration';
import type {
  MaterialConsumptionRow,
  ProductionOrderRegisterRow,
  ProductionReceiptRegisterRow,
  ProductionYieldRow,
} from '@/types/reports';
import { withId } from './reportHelpers';

const TABS = ['productionOrderRegister', 'productionYield', 'materialConsumption', 'productionReceiptRegister'] as const;

export default function ProductionReportsPage() {
  const { t } = useTranslation('reports');
  const { formatDate } = useDateFormat();
  const { data: processConfiguration } = useProcessConfiguration();
  const showClientColumn = Boolean(processConfiguration?.thirdPartyLogisticsEnabled);
  const [tab, setTab] = useState(0);

  const { data: orders = [], isLoading: loadingOrders } = useQuery({
    queryKey: ['reports', 'production-order-register'],
    queryFn: async () => (await apiClient.get<ProductionOrderRegisterRow[]>('/reports/production-order-register')).data,
    enabled: tab === 0,
  });
  const { data: yields = [], isLoading: loadingYield } = useQuery({
    queryKey: ['reports', 'production-yield'],
    queryFn: async () => (await apiClient.get<ProductionYieldRow[]>('/reports/production-yield')).data,
    enabled: tab === 1,
  });
  const { data: consumption = [], isLoading: loadingConsumption } = useQuery({
    queryKey: ['reports', 'material-consumption'],
    queryFn: async () => (await apiClient.get<MaterialConsumptionRow[]>('/reports/material-consumption')).data,
    enabled: tab === 2,
  });
  const { data: receipts = [], isLoading: loadingReceipts } = useQuery({
    queryKey: ['reports', 'production-receipt-register'],
    queryFn: async () => (await apiClient.get<ProductionReceiptRegisterRow[]>('/reports/production-receipt-register')).data,
    enabled: tab === 3,
  });

  const orderColumns: DataTableColumn<ReturnType<typeof withId<ProductionOrderRegisterRow>>[number]>[] = [
    { field: 'orderNumber', headerName: t('columns.number') },
    { field: 'materialCode', headerName: t('columns.materialCode') },
    { field: 'materialName', headerName: t('columns.materialName') },
    { field: 'warehouseName', headerName: t('columns.warehouse'), filterable: true },
    { field: 'plannedDate', headerName: t('columns.date'), render: (r) => formatDate(r.plannedDate) },
    { field: 'status', headerName: t('columns.status'), render: (r) => (r.isClosed ? <StatusChip status="Closed" /> : <StatusChip status={r.status} />), filterable: true },
    { field: 'plannedQty', headerName: t('columns.plannedQty') },
    { field: 'producedQty', headerName: t('columns.producedQty') },
    ...(showClientColumn
      ? [{ field: 'clientName', headerName: t('columns.client'), render: (r: ReturnType<typeof withId<ProductionOrderRegisterRow>>[number]) => r.clientName ?? '—', filterable: true } as DataTableColumn<ReturnType<typeof withId<ProductionOrderRegisterRow>>[number]>]
      : []),
  ];

  const yieldColumns: DataTableColumn<ReturnType<typeof withId<ProductionYieldRow>>[number]>[] = [
    { field: 'orderNumber', headerName: t('columns.number') },
    { field: 'materialCode', headerName: t('columns.materialCode') },
    { field: 'materialName', headerName: t('columns.materialName') },
    { field: 'plannedQty', headerName: t('columns.plannedQty') },
    { field: 'producedQty', headerName: t('columns.producedQty') },
    { field: 'acceptedQty', headerName: t('columns.acceptedQty') },
    { field: 'rejectedQty', headerName: t('columns.rejectedQty') },
    { field: 'yieldPercent', headerName: t('columns.yieldPercent'), render: (r) => (r.yieldPercent != null ? `${r.yieldPercent}%` : '—') },
    { field: 'rejectionPercent', headerName: t('columns.rejectionRate'), render: (r) => (r.rejectionPercent != null ? `${r.rejectionPercent}%` : '—') },
  ];

  const consumptionColumns: DataTableColumn<ReturnType<typeof withId<MaterialConsumptionRow>>[number]>[] = [
    { field: 'orderNumber', headerName: t('columns.number') },
    { field: 'producedMaterialCode', headerName: t('columns.producedMaterial'), filterable: true },
    { field: 'componentMaterialCode', headerName: t('columns.materialCode') },
    { field: 'componentMaterialName', headerName: t('columns.materialName') },
    { field: 'requiredQty', headerName: t('columns.requiredQty') },
    { field: 'issuedQty', headerName: t('columns.issuedQty') },
    { field: 'varianceQty', headerName: t('columns.variance') },
    { field: 'variancePercent', headerName: t('columns.variancePercent'), render: (r) => (r.variancePercent != null ? `${r.variancePercent}%` : '—') },
  ];

  const receiptColumns: DataTableColumn<ReturnType<typeof withId<ProductionReceiptRegisterRow>>[number]>[] = [
    { field: 'receiptNumber', headerName: t('columns.number') },
    { field: 'productionOrderNumber', headerName: t('columns.source'), render: (r) => r.productionOrderNumber ?? r.materialRequestNumber ?? '—' },
    { field: 'warehouseName', headerName: t('columns.warehouse'), filterable: true },
    { field: 'receiptDate', headerName: t('columns.date'), render: (r) => formatDate(r.receiptDate) },
    { field: 'status', headerName: t('columns.status'), render: (r) => <StatusChip status={r.status} />, filterable: true },
    { field: 'materialCode', headerName: t('columns.materialCode') },
    { field: 'materialName', headerName: t('columns.materialName') },
    { field: 'producedQty', headerName: t('columns.producedQty') },
    { field: 'acceptedQty', headerName: t('columns.acceptedQty') },
    { field: 'rejectedQty', headerName: t('columns.rejectedQty') },
    { field: 'batchNo', headerName: t('columns.batch'), render: (r) => r.batchNo ?? '—' },
  ];

  return (
    <>
      <PageHeader title={t('modules.production.title')} subtitle={t('modules.production.subtitle')} />

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }} variant="scrollable" scrollButtons="auto">
        {TABS.map((key) => (
          <Tab key={key} label={t(`${key}.title`)} />
        ))}
      </Tabs>

      <Box hidden={tab !== 0}>
        <DataTable columns={orderColumns} rows={withId(orders, (r) => r.productionOrderId)} loading={loadingOrders} searchable exportFileName="production-order-register" />
      </Box>
      <Box hidden={tab !== 1}>
        <DataTable columns={yieldColumns} rows={withId(yields, (r) => r.productionOrderId)} loading={loadingYield} searchable exportFileName="production-yield" />
      </Box>
      <Box hidden={tab !== 2}>
        <DataTable columns={consumptionColumns} rows={withId(consumption, (r) => `${r.productionOrderId}-${r.componentMaterialCode}`)} loading={loadingConsumption} searchable exportFileName="material-consumption" />
      </Box>
      <Box hidden={tab !== 3}>
        <DataTable columns={receiptColumns} rows={withId(receipts, (r) => `${r.receiptId}-${r.materialCode}`)} loading={loadingReceipts} searchable exportFileName="production-receipt-register" />
      </Box>
    </>
  );
}
